import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as base64 from 'base-64';
import * as utf8 from 'utf8';
import * as confetti from 'canvas-confetti';
import { environment } from 'src/environments/environment';
import { SessionstorageDosService } from 'src/service/sessionstorage-dos.service';
import { BoletosColaboradoresService } from '../boletos-colaboradores/services/boletos-colaboradores.service';
import { Boleto } from '../models/Boleto';
import { LocalstorageService } from '../services/localstorage.service';
import { MetodosLigaColaborador } from '../services/metodos-liga-colaborador';
import { ModoLigaApp } from '../services/modo-liga-app';
import { SessionstorageService } from '../services/sessionstorage.service';
import { TimerService } from '../timer/timer.service';

/**
 * VENTA DIGITAL EXTERNA (plazas / tiendas).
 *
 * Se llega aqui desde la liga de colaborador (boletos-colaborador/<PK1 base64>) cuando el
 * PK1 esta en environment.ventaDigitalExterna. El redirect vive en
 * BoletosColaboradoresComponent.ngOnInit.
 *
 * DISEÑO: el de la campaña (banner + maquina de la suerte) y, abajo, la lista de boletos
 * del colaborador como segunda opcion (elegir uno o varios a mano).
 * FUNCIONALIDAD: la de la liga de colaborador. La maquina NO saca boletos publicos al azar:
 * gira sobre los boletos de ESE colaborador (api/BoletosColaborador), ya sin los que traen un
 * pago en proceso (mismo validador ValidarFoliosReferencia que la liga), y al pagar hace
 * exactamente lo que hace la liga (BoletosRecomendados, timer, BoletosCol=1, carrito).
 *
 * ViewEncapsulation.None: los numeros de los rodillos se agregan por DOM durante el giro y
 * no llevarian el atributo de encapsulado. Todo el SCSS va bajo .vde para no salirse.
 */
@Component({
  selector: 'app-venta-digital-externa',
  templateUrl: './venta-digital-externa.component.html',
  styleUrls: ['./venta-digital-externa.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VentaDigitalExternaComponent implements OnInit, OnDestroy {
  /** Alto de cada numero en el rodillo. Debe coincidir con $vde-num-h del SCSS. */
  private static readonly ALTO_NUMERO = 70;
  /** Vueltas completas (0-9) que ya trae cada rodillo antes de caer en su digito. */
  private static readonly VUELTAS_BASE = 2;
  /** Duracion del giro del primer rodillo; cada rodillo siguiente tarda ESCALON_SEG mas. */
  private static readonly GIRO_SEG = 4.5;
  private static readonly ESCALON_SEG = 0.35;

  public readonly digitosBase = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  public readonly rodillos = [0, 1, 2, 3, 4, 5];

  public clave_colaborador: number = 0;
  public clave_colaborador_encoded: string;
  public nombre_colaborador: string = '';

  /** Boletos del colaborador que se pueden vender hoy (ya validados). */
  public disponibles: Array<Boleto> = [];
  /** El que acaba de salir en la maquina. */
  public boletoActual: Boleto = null;

  public cargando = true;
  public girando = false;
  public sinBoletos = false;
  public errorCarga = false;
  public validando = false;
  public mensajesNoDisponibles: Array<string> = [];

  /** Lista de boletos (segunda opcion, debajo de la maquina): folios marcados y filtro. */
  public seleccionLista = new Set<string>();
  public filtroLista = '';
  public validandoLista = false;
  public mensajesLista: Array<string> = [];

  private timeouts: any[] = [];
  private subMenuDisplay: string = null;
  /** Espacio para que el header (position: fixed) no tape el banner. */
  public espacioHeader = 80;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _session: SessionstorageService,
    private _local: LocalstorageService,
    private _BColaboradoresService: BoletosColaboradoresService,
    private _sessionDos: SessionstorageDosService,
    public st: TimerService
  ) {}

  ngOnInit(): void {
    this.clave_colaborador_encoded = this.route.snapshot.params.id;
    this.clave_colaborador = VentaDigitalExternaComponent.decodificarClave(this.clave_colaborador_encoded);

    // Un ID que no esta en la lista no tiene nada que hacer aqui: a su liga normal.
    if (!VentaDigitalExternaComponent.esVentaExterna(this.clave_colaborador)) {
      this.router.navigate(['boletos-colaborador', this.clave_colaborador_encoded], { replaceUrl: true });
      return;
    }

    // Candado: desde aqui solo puede ir por el flujo de compra (carrito, datos, pago).
    // Si intenta ir al home o a otra pagina, ModoLigaAppGuard lo regresa a esta maquina.
    ModoLigaApp.activar('/venta-digital-externa/' + this.clave_colaborador_encoded, 'externa');

    // El diseño va directo del header al banner: se esconde la barra glass de abajo del
    // header y se restaura al salir (ngOnDestroy), para no afectar otras pantallas.
    const subMenu = document.querySelector('.sub-menu') as HTMLElement;
    if (subMenu) {
      this.subMenuDisplay = subMenu.style.display;
      subMenu.style.display = 'none';
    }
    this.ajustarEspacioHeader();

    this.cargarBoletos();
  }

  ngOnDestroy(): void {
    this.timeouts.forEach((t) => clearTimeout(t));
    const subMenu = document.querySelector('.sub-menu') as HTMLElement;
    if (subMenu) subMenu.style.display = this.subMenuDisplay || '';
  }

  /**
   * El header es fijo y su alto cambia con el ancho (logo, menu). En las demas paginas la
   * barra .sub-menu hacia de colchon; aqui se esconde, asi que se mide el header real.
   */
  @HostListener('window:resize')
  ajustarEspacioHeader() {
    const header = document.getElementById('main-header');
    if (header) this.espacioHeader = Math.max(header.getBoundingClientRect().bottom, 0);
  }

  /** PK1 en base64 (como lo arma la App) -> numero. 0 si no se puede leer. */
  static decodificarClave(encoded: string): number {
    try {
      return Number(utf8.decode(base64.decode(decodeURIComponent(encoded || ''))));
    } catch (e) {
      return 0;
    }
  }

  static esVentaExterna(pk: number): boolean {
    return !!pk && ((environment as any).ventaDigitalExterna || []).includes(pk);
  }

  // ---------------------------------------------------------------- carga

  private cargarBoletos() {
    this.cargando = true;
    this.errorCarga = false;

    this._BColaboradoresService.getBoletosColaborador(this.clave_colaborador).subscribe(
      (data: Array<Boleto>) => {
        // Igual que la liga: se registra la liga para "volver a mis boletos" en error-pago.
        // Apunta DIRECTO a esta maquina y en el MISMO sitio (window.location.origin), no a
        // environment.urlCompartirBoletos: ese es un dominio fijo (en local apunta a QA) y
        // sacaba al comprador a otro micrositio, donde caia en el diseño viejo de la liga.
        sessionStorage.setItem(
          'liga_compartida_colaborador',
          window.location.origin + '/venta-digital-externa/' + this.clave_colaborador_encoded
        );
        this._sessionDos.actualizarEstadoBloqueo();
        this._BColaboradoresService.UpdateBitacora(this.clave_colaborador).subscribe(() => {}, () => {});

        this.quitarBoletosEnEspera(data || []);
      },
      () => {
        this.cargando = false;
        this.errorCarga = true;
      }
    );

    this._BColaboradoresService.getNombreColaborador(this.clave_colaborador).subscribe(
      (nombre: string) => {
        this.nombre_colaborador = nombre;
        sessionStorage.setItem('liga_compartida_nombre_colaborador', nombre);
        this._sessionDos.actualizarEstadoBloqueo();
      },
      () => {}
    );
  }

  /**
   * Misma regla que la liga de colaborador: los boletos con un pago sin resolver (tarjeta,
   * referencia, OXXO, SPEI, liga...) no entran a la maquina. Si la validacion falla no se
   * quita nada; la validacion al pagar sigue siendo la red de seguridad.
   */
  private quitarBoletosEnEspera(boletos: Array<Boleto>) {
    const folios = boletos.map((b) => this.folioTexto(b)).filter((f) => f.length > 0);

    if (folios.length === 0) {
      this.disponibles = [];
      this.terminarCarga();
      return;
    }

    this._BColaboradoresService.validarBoletosODC({ folios: folios.join(','), idCarrito: '' }).subscribe(
      (resultado: Array<{ folio: string; valido: boolean }>) => {
        const bloqueados = new Set(
          (resultado || []).filter((r) => !r.valido).map((r) => r.folio.toString().trim())
        );
        this.disponibles = boletos.filter((b) => !bloqueados.has(this.folioTexto(b)));
        this.terminarCarga();
      },
      () => {
        this.disponibles = boletos;
        this.terminarCarga();
      }
    );
  }

  private terminarCarga() {
    this.cargando = false;
    this.sinBoletos = this.disponibles.length === 0;
  }

  // ---------------------------------------------------------------- maquina

  get puedeGirar(): boolean {
    return !this.cargando && !this.girando && !this.validando && this.disponibles.length > 0;
  }

  activar() {
    if (!this.puedeGirar) return;

    // Si hay mas de uno, que no repita el que acaba de salir.
    let candidatos = this.disponibles;
    if (candidatos.length > 1 && this.boletoActual) {
      const anterior = this.folioTexto(this.boletoActual);
      candidatos = candidatos.filter((b) => this.folioTexto(b) !== anterior);
    }
    const boleto = candidatos[Math.floor(Math.random() * candidatos.length)];

    this.boletoActual = null;
    this.girando = true;
    this.mensajesNoDisponibles = [];
    this.girarRodillos(this.folioTexto(boleto).padStart(6, '0').slice(-6));

    this.programar(() => {
      this.girando = false;
      this.boletoActual = boleto;
      this.lanzarConfetti();
    }, (VentaDigitalExternaComponent.GIRO_SEG + 5 * VentaDigitalExternaComponent.ESCALON_SEG) * 1000 + 150);
  }

  /**
   * Cada rodillo trae 0-9 dos veces. Para caer en el digito d se le agregan 0..d al final
   * y se desplaza hasta ese ultimo: -(20 + d) * alto. Cada rodillo tarda un poco mas que
   * el anterior para que vayan parando de izquierda a derecha.
   */
  private girarRodillos(folio: string) {
    const alto = VentaDigitalExternaComponent.ALTO_NUMERO;
    const base = this.digitosBase.length * VentaDigitalExternaComponent.VUELTAS_BASE;

    this.rodillos.forEach((i) => {
      const lista = document.getElementById('vde-n' + i);
      if (!lista) return;

      // Limpiar el giro anterior: quitar los numeros agregados y la animacion.
      lista.querySelectorAll('li.vde-extra').forEach((li) => li.remove());
      lista.classList.remove('vde-girar');
      void lista.offsetHeight; // reinicia la animacion

      const digito = parseInt(folio[i], 10) || 0;
      for (let d = 0; d <= digito; d++) {
        const li = document.createElement('li');
        li.className = 'vde-extra';
        li.textContent = d.toString();
        lista.appendChild(li);
      }

      lista.style.setProperty('--vde-fin', -(base + digito) * alto + 'px');
      lista.style.setProperty('--vde-duracion', VentaDigitalExternaComponent.GIRO_SEG + i * VentaDigitalExternaComponent.ESCALON_SEG + 's');
      lista.classList.add('vde-girar');
    });
  }

  // ---------------------------------------------------------------- carrito

  /**
   * Boton de la maquina despues del giro. Igual que BoletosColaboradoresComponent.add():
   * valida que el boleto siga libre y lo manda al carrito con las mismas llaves de sesion
   * que la liga de colaborador. Si ya no esta libre, se saca de la maquina y se avisa.
   */
  agregarAlCarrito() {
    const boleto = this.boletoActual;
    if (!boleto || this.validando) return;

    this.validando = true;
    this.mensajesNoDisponibles = [];
    this.validarYAgregar(
      [boleto],
      'Maquina de la suerte venta digital externa',
      (mensajes) => {
        this.validando = false;
        this.mensajesNoDisponibles = mensajes.map((m) => m + ' Activa la máquina para sacar otro.');
        this.boletoActual = null;
      }
    );
  }

  // ---------------------------------------------------------------- lista

  /** Boletos para la lista: disponibles, ordenados y filtrados por lo que escriba. */
  get boletosLista(): Array<Boleto> {
    const filtro = (this.filtroLista || '').replace(/\D/g, '');
    return this.disponibles
      .filter((b) => !filtro || this.folioTexto(b).includes(filtro))
      .sort((a, b) => this.folioTexto(a).localeCompare(this.folioTexto(b)));
  }

  estaSeleccionado(b: Boleto): boolean {
    return this.seleccionLista.has(this.folioTexto(b));
  }

  alternar(b: Boleto) {
    if (this.validandoLista) return;
    const f = this.folioTexto(b);
    if (this.seleccionLista.has(f)) this.seleccionLista.delete(f);
    else this.seleccionLista.add(f);
    this.mensajesLista = [];
  }

  limpiarSeleccion() {
    if (this.validandoLista) return;
    this.seleccionLista.clear();
    this.mensajesLista = [];
  }

  get totalLista(): number {
    return this.disponibles
      .filter((b) => this.estaSeleccionado(b))
      .reduce((t, b) => t + (Number(b.costo) || 0), 0);
  }

  agregarSeleccionados() {
    if (this.seleccionLista.size === 0 || this.validandoLista) return;
    const boletos = this.disponibles.filter((b) => this.estaSeleccionado(b));

    this.validandoLista = true;
    this.mensajesLista = [];
    this.validarYAgregar(boletos, 'Lista de boletos venta digital externa', (mensajes) => {
      this.validandoLista = false;
      this.mensajesLista = mensajes;
    });
  }

  // ---------------------------------------------------------------- validar y al carrito

  /**
   * Igual que BoletosColaboradoresComponent.add(): valida que sigan libres
   * (ValidarFoliosReferencia) y, si todos lo estan, van al carrito. Los que ya no estan
   * libres se sacan de la maquina y de la lista, y se avisa por que (onRechazo).
   */
  private validarYAgregar(boletos: Array<Boleto>, tipoBoleto: string, onRechazo: (mensajes: Array<string>) => void) {
    const folios = boletos.map((b) => this.folioTexto(b)).join(',');

    this._BColaboradoresService.validarBoletosODC({ folios, idCarrito: '' }).subscribe(
      (resultado: Array<{ folio: string; valido: boolean; mensaje: string }>) => {
        const invalidos = (resultado || []).filter((r) => !r.valido);

        if (invalidos.length === 0) {
          this.continuarAlCarrito(boletos, tipoBoleto);
          return;
        }

        const malos = new Set(invalidos.map((r) => r.folio.toString().trim()));
        this.disponibles = this.disponibles.filter((b) => !malos.has(this.folioTexto(b)));
        malos.forEach((f) => this.seleccionLista.delete(f));
        this.sinBoletos = this.disponibles.length === 0;
        onRechazo(invalidos.map((r) => r.mensaje || `El boleto ${r.folio} ya no está disponible.`));
      },
      () => onRechazo(['No pudimos validar tus boletos. Intenta de nuevo en un momento.'])
    );
  }

  private continuarAlCarrito(boletos: Array<Boleto>, tipoBoleto: string) {
    const lsRecomendados = this._local.getBoletosRecomendados();
    const recomendados: Array<Boleto> = lsRecomendados == null ? [] : JSON.parse(lsRecomendados);
    for (const boleto of boletos) {
      if (!recomendados.some((r) => r.folio === boleto.folio)) recomendados.push(boleto);
    }

    const date = new Date();
    this._local.setFechaInicio(date);
    this._local.setFechaFin(date);
    this._local.setBoletosRecomendados(recomendados);
    // Igual que la liga: forma-pago aplica environment.metodosPagoLigaColaborador a estos folios.
    MetodosLigaColaborador.marcar(boletos);
    this.st.startTimer();
    this._session.setBoletosCol(1);
    this._session.setTiposPago(1);

    // Mismas llaves que la liga de colaborador (sin 'pk_boleto_app': esto NO es la App).
    sessionStorage.setItem('pagina', 'Liga colaborador - venta digital externa');
    sessionStorage.setItem('tipoBoleto', tipoBoleto);
    sessionStorage.setItem('texto', 'Gracias por sumarte a Sorteo Anáhuac, con tu boleto ayudaras a muchos estudiantes');
    this.router.navigate(['carrito-compra']);
  }

  // ---------------------------------------------------------------- utilidades

  folioTexto(b: Boleto): string {
    return ((b && b.folio) || '').toString().trim();
  }

  private programar(fn: () => void, ms: number) {
    this.timeouts.push(setTimeout(fn, ms));
  }

  private lanzarConfetti() {
    try {
      const confettiFunc = (confetti as any).default || confetti;
      confettiFunc({
        particleCount: 120,
        spread: 75,
        origin: { y: 0.6 },
        colors: ['#ff5900', '#662480', '#ffffff', '#ffb300'],
      });
    } catch (e) {
      // El confetti es adorno: si falla, no pasa nada.
    }
  }
}
